import { Component, ChangeDetectionStrategy, ChangeDetectorRef, viewChild, ElementRef, input, signal, computed, afterNextRender, DestroyRef, inject } from '@angular/core';
import { trigger, transition, style, animate, query, stagger } from '@angular/animations';
import { IBrandCard } from '../../types';
import { ResizeService, AuthService } from '../../auth.service';
import { RouterLink } from '@angular/router';
import { debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BucketPipe } from '../../bucket.pipe';
import { Platform } from '@angular/cdk/platform';
import { ImageFallbackDirective } from '../../image-fallback.directive';

@Component({
  selector: 'brand-carousel',
  templateUrl: 'brand-carousel.component.html',
  styleUrls: ['brand-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [RouterLink, BucketPipe, ImageFallbackDirective],
  animations: [
    trigger('animator', [
      transition(':increment', [
        query(
          ':enter',
          [
            style({opacity: 0, transform: 'translateY(-100%)'}),
            stagger(50, [animate('300ms ease-out', style({opacity: 1, transform: 'translateY(0%)'}))]),
          ],
          {optional: true},
        ),
        // query(':leave', [stagger(50, [animate('300ms ease-out', style({opacity: 0, transform: 'translateY(100%)'}))])]),
      ]),
      transition(':decrement', [
        query(
          ':enter',
          [
            style({opacity: 0, transform: 'translateY(-100%)'}),
            stagger(50, [animate('300ms ease-out', style({opacity: 1, transform: 'translateY(0%)'}))]),
          ],
          {optional: true},
        ),
        // query(':leave', [stagger(50, [animate('300ms ease-out', style({opacity: 0, transform: 'translateY(100%)'}))])]),
      ]),
    ]),
  ],
})

export class BrandCarouselComponent {
  readonly destroyRef = inject(DestroyRef);
  size = inject(ResizeService);
  platform = inject(Platform);

  carousel = viewChild<ElementRef<HTMLDivElement>>('carousel');
  brands = input<IBrandCard[]|null>(null);
  width = input<number>(100);
  height = input<number>(100);
  rows = input<number>(1);
  interval = input<number>(6000);
  squared = input<boolean>(false);

  index = signal<number>(-1);
  lastKnownSize = signal<number>(0);

  currentBrands = computed(() => {
    const brands = [...this.brands()||[]];
    const index = Math.max(this.index(), 0);
    const endIndex = index + this.lastKnownSize() || brands.length;
    const overflow = Math.max(0, endIndex - brands.length);

    const current = [...brands.slice(index, endIndex), ...brands.slice(0, overflow)];
    return current.map((c,i) => {
      const x = Object.assign({}, c);
      x._id =Date.now().toString() + i;//we need _id to be unique with each iteration
      return x;
    });
  });

  empty = signal<number[]>(Array(20).fill(1));
  looper: any = null;

  constructor(public ss: AuthService, private changes: ChangeDetectorRef) {
    
    this.size.resizer.pipe(takeUntilDestroyed(), debounceTime(300)).subscribe(() => {
      this.calculateSize();
    });

    
    afterNextRender({
      read: () => {
        this.calculateSize();
        this.play();
      }
    });
    this.destroyRef.onDestroy(() => this.pause());
  }

  calculateSize(): void {
    const carousel = this.carousel();

    if(carousel && carousel.nativeElement?.getBoundingClientRect){
      const carouselWidth = carousel.nativeElement.getBoundingClientRect().width || (this.width()+20);
  
      this.lastKnownSize.set(Math.floor(carouselWidth / (this.width()+20)));
      this.empty.set(Array(this.lastKnownSize()).fill(1));
    }
  }

  doLoop = () => {
    this.calculateSize();

    this.index.update(index => (index + this.lastKnownSize()) % (this.brands()?.length??1));

    this.changes.markForCheck();
  }

  pause(){
    if(this.looper){
      clearInterval(this.looper);
    }
    this.looper = null;
    this.changes.markForCheck();
  }

  play(){
    if(this.looper || !this.platform.isBrowser || !this.interval()){
      return;
    }
    this.looper = setInterval(this.doLoop, this.interval());
  }
}
